// Environment configuration utility
// This will be replaced at build time with the appropriate environment config

const gd = {
  environment: (window.location.hostname).includes('qa') ? 'qa' : 'prod'
};
const currentEnv = gd.environment;
let config = {
  environment:  currentEnv,
  API_BASE_URL: 'https://qaintaz-oci.aetna.com',
  // QA points at the APIC Stress environment as of July 2026 (see "APIC Stress Env
  // needed for MCS PT testing" email from Naga Grandhi, 7/10/26) — strapi10.cvshealth.com,
  // /medicaid/stress, rather than the old qaapi10.cvshealth.com/medicaid/qapath1. Prod is
  // unaffected and stays on api10.cvshealth.com/medicaid/prod; the email notes prod "will
  // be similar to stress" once its own stress-equivalent details are confirmed.
  APIC_BASE_URL: currentEnv === 'prod' ? 'https://api10.cvshealth.com' : 'https://strapi10.cvshealth.com',
  APIC_BASE_URL_API: currentEnv === 'prod' ? 'https://api10.cvshealth.com/medicaid/prod' : 'https://strapi10.cvshealth.com/medicaid/stress',
  // The geolocation reverse/forward-geocode endpoints (coordinates & addresses) are hosted
  // under an EXTRA /medicaid/qapath1 segment on the stress environment that the rest of
  // the APIC endpoints don't have (e.g.
  // .../medicaid/stress/medicaid/qapath1/hcb/v3/geolocation/geocodes/coordinates vs. plain
  // .../medicaid/stress/hcb/v3/planfeature/plans/FL/features). Rather than special-case
  // that one extra segment inline at each call site, this second base captures it once;
  // see locationService.js's reverseGeocodeMCS/geocodeMCS, the only two places that use it.
  // NOTE: the facet search/retrieve endpoint was originally believed to need this too, but
  // that was a typo from the other team — it actually uses the plain APIC_BASE_URL_API base
  // like everything else. See facetsService.js's fetchFacets.
  APIC_BASE_URL_API_V2: currentEnv === 'prod' ? 'https://api10.cvshealth.com/medicaid/prod' : 'https://strapi10.cvshealth.com/medicaid/stress/medicaid/qapath1',
  // Stress client_id from the same email; prod keeps its own existing value until a
  // dedicated prod client_id is confirmed.
  VITE_APIC_CLIENT_ID: currentEnv === 'prod' ? '5b6f3d96e22256a1b1ddef85774664f0' : 'b9f601560a41a3d0a4dd390931c5ddb9',
  VITE_ADOBE_LAUNCH_SCRIPTS: currentEnv === 'prod' ? 'https://assets.adobedtm.com/99b399019383/6ce4eb7bbb41/launch-5ba4d85a6890.min.js' : 'https://assets.adobedtm.com/99b399019383/6ce4eb7bbb41/launch-39ee7ed5f1f0-staging.min.js',
  SIGNUP_URL: '/signup',
  SIGNIN_URL: '/signin',
  AUTH_REDIRECT_URL: '/auth/callback', // OAuth redirect URL for receiving authorization code
  SEARCH_API_CONFIG: {
    endpoint: '/hcb/v3/providerfuzzysearch/providers',
    actualSearchEndpoint: '/hcb/v3/providersearch/providers/search/retrieve',
    providerSearchEndpoint: '/hcb/v3/providersearch/providers/search/retrieve',
    facetsEndpoint: '/hcb/v3/providersearch/providers/facet/search/retrieve'
  }
};

// Use Vite environment variables if available
if (import.meta.env.VITE_API_BASE_URL) {
  config.API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
}

if (import.meta.env.VITE_APIC_BASE_URL) {
  config.APIC_BASE_URL = import.meta.env.VITE_APIC_BASE_URL;
}

if (import.meta.env.VITE_APIC_CLIENT_ID) {
  config.APIC_CLIENT_ID = import.meta.env.VITE_APIC_CLIENT_ID;
}

if (import.meta.env.VITE_APP_ENV) {
  config.environment = import.meta.env.VITE_APP_ENV;
}

// Try to load config from global window object if available (for runtime config)
if (typeof window !== 'undefined' && window.APP_CONFIG) {
  config = { ...config, ...window.APP_CONFIG };
}

// Environment detection fallback
const getEnvironment = () => {
  // Check Vite environment variables first
  if (import.meta.env.VITE_APP_ENV) {
    return import.meta.env.VITE_APP_ENV;
  }
  
  // Check if we're in development mode
  if (import.meta.env.DEV) {
    return 'dev';
  }
  
  // Check hostname to determine environment
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname.includes('qa')) {
      return 'qa';
    } else if (hostname.includes('dev')) {
      return 'dev';
    } else if (hostname.includes('prod') || hostname.includes('aetapps')) {
      return 'prod';
    }
  }
  
  // Default to production for unknown environments
  return 'prod';
};

// Auto-detect environment and set appropriate API base URL
const autoDetectConfig = () => {
  const env = getEnvironment();
  
  const envConfigs = {
    dev: {
      environment: 'dev',
      API_BASE_URL: 'https://devintaz-oci.aetna.com',
      // Intentionally no PLAN_PICKER_URL for dev. Note `getEnvironment()` above returns
      // 'dev' whenever `import.meta.env.DEV` is true — i.e. ANY local `npm run dev`
      // session lands in this same bucket too, not just the deployed dev tier. There is
      // no dedicated dev-branded plan-picker page, and dev/local sit below qa in the
      // promotion pipeline, so silently bouncing a developer's local session (or a
      // dev-tier visitor) up to the real qa.aetnabetterhealth.com — as this used to do —
      // is exactly the "redirecting to qa from dev and local" behavior we don't want.
      // App.jsx's redirectToPlanPicker() helper treats a falsy PLAN_PICKER_URL as "stay
      // put, don't navigate anywhere" rather than defaulting to some other tier's URL.
      PLAN_PICKER_URL: null,
      SEARCH_API_CONFIG: {
        endpoint: '/hcb/v3/providerfuzzysearch/providers',
        actualSearchEndpoint: '/hcb/v3/providersearch/providers/search/retrieve',
        providerSearchEndpoint: '/hcb/v3/providersearch/providers/search/retrieve',
        facetsEndpoint: '/hcb/v3/providersearch/providers/facet/search/retrieve'
      }
    },
    qa: {
      environment: 'qa',
      API_BASE_URL: 'https://qaintaz-oci.aetna.com',
      PLAN_PICKER_URL: 'https://qa.aetnabetterhealth.com/find-care-test.html',
      SEARCH_API_CONFIG: {
        endpoint: '/hcb/v3/providerfuzzysearch/providers',
        actualSearchEndpoint: '/hcb/v3/providersearch/providers/search/retrieve',
        providerSearchEndpoint: '/hcb/v3/providersearch/providers/search/retrieve',
        facetsEndpoint: '/hcb/v3/providersearch/providers/facet/search/retrieve'
      }
    },
    prod: {
      environment: 'prod',
      API_BASE_URL: 'https://intaz-oci.aetna.com',
      PLAN_PICKER_URL: 'https://www.aetnabetterhealth.com/find-provider.html',
      SEARCH_API_CONFIG: {
        envId: '30', // Production environment ID
        endpoint: '/hcb/v3/providerfuzzysearch/providers',
        actualSearchEndpoint: '/hcb/v3/providersearch/providers/search/retrieve',
        providerSearchEndpoint: '/hcb/v3/providersearch/providers/search/retrieve',
        facetsEndpoint: '/hcb/v3/providersearch/providers/facet/search/retrieve'
      }
    }
  };
  
  return { ...config, ...envConfigs[env] };
};

// Use auto-detected configuration only if Vite env vars are not available
if (!import.meta.env.VITE_API_BASE_URL) {
  config = autoDetectConfig();
}

// Add global debug function
if (typeof window !== 'undefined') {
  window.debugEnvironment = () => {
    const debugInfo = {
      environment: config.environment,
      isDev: import.meta.env.DEV,
      apiBaseUrl: config.API_BASE_URL,
      searchConfig: config.SEARCH_API_CONFIG,
      usesRealAPI: true // No fallback data
    };
    return config;
  };
}

// Helper function to build search API URLs
export const buildSearchApiUrl = (endpoint, params = {}) => {
  const searchConfig = config.SEARCH_API_CONFIG;
  const defaultParams = {
    ...(searchConfig.envId && { envId: searchConfig.envId }),
    page: 1,
    size: 20
  };

  const finalParams = { ...defaultParams, ...params };
  const queryString = Object.entries(finalParams)
    .filter(([, value]) => value !== null && value !== undefined)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
  
  const fullEndpoint = endpoint.startsWith('/') ? endpoint : searchConfig[endpoint] || endpoint;
  return `${config.API_BASE_URL}${fullEndpoint}?${queryString}`;
};

// Helper function to get full OAuth redirect URL
export const getAuthRedirectUrl = () => {
  if (typeof window === 'undefined') {
    return config.AUTH_REDIRECT_URL;
  }
  
  // Build full URL with current origin
  const origin = window.location.origin;
  const basePath = window.location.pathname.split('/')[1] === 'mcsui' ? '/mcsui' : '';
  
  return `${origin}${basePath}${config.AUTH_REDIRECT_URL}`;
};

export default config;